import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "./blog.css";
import { Container, Pagination } from "react-bootstrap";
import authorAvatar from "../../assets/images/icons/app-icon.png";

const DEFAULT_AUTHOR_NAME = "Tanaka Misaki";
const DEFAULT_SITE_FILTER = "&filters[$or][0][site][$null]=true&filters[$or][1][site][$eq]=&filters[$or][2][site][$eq]=ikiai.org";

function extractExcerpt(content, maxLength = 160) {
  if (!content) return "";
  // Strip markdown syntax for a clean excerpt
  const plain = content
    .replace(/#{1,6}\s+/g, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[|`~>-]/g, "")
    .replace(/\n+/g, " ")
    .trim();
  return plain.length > maxLength
    ? plain.substring(0, maxLength).trimEnd() + "\u2026"
    : plain;
}

export default function BlogList() {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Scroll to the top immediately upon pagination click
    window.scrollTo({ top: 0, behavior: "smooth" });

    const fetchBlogs = async () => {
      setLoading(true);
      // Set page size to 12
      const reqUrl = `${process.env.REACT_APP_STRAPI_URL}/api/posts?locale=${i18n.language}${DEFAULT_SITE_FILTER}&fields=title,slug,updatedAt,content&populate=coverImage&sort=updatedAt:desc&pagination[page]=${page}&pagination[pageSize]=12`;
      try {
        const res = await fetch(reqUrl);
        const json = await res.json();
        if (json.data) {
          setBlogs(json.data);
          if (json.meta && json.meta.pagination) {
            setPageCount(json.meta.pagination.pageCount);
          }
        } else {
          setBlogs([]);
          setPageCount(1);
        }
      } catch (error) {
        console.error("Blog fetch failed:", error);
        setBlogs([]);
        setPageCount(1);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [i18n.language, page]);

  const formatDate = (date) => {
    if (!date) return t("blog.unknown_date");
    const d = new Date(date);
    if (isNaN(d.getTime())) return t("blog.invalid_date");
    return d.toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getCoverUrl = (blog) => {
    const cover = blog.coverImage;
    if (!cover) return null;
    // Strapi v5 media format
    const url = cover.formats?.medium?.url || cover.formats?.small?.url || cover.url;
    if (!url) return null;
    // Handle relative URLs
    return url.startsWith("http") ? url : `${process.env.REACT_APP_STRAPI_URL}${url}`;
  };

  if (!Array.isArray(blogs)) {
    return <div>{t("blog.no_blog_data")}</div>;
  }

  return (
    <Container className="blog-container">
      <div className="maxw">
        {/* <div className="blog-list-header">
          <h1 className="global-title">{t("blog.blog_list_title")}</h1>
        </div> */}

        {loading && blogs.length === 0 ? (
          <div className="blog-loading-container">
            <div className="blog-loading-spinner"></div>
          </div>
        ) : (
          <div
            className="blog-list-grid"
            style={{
              opacity: loading ? 0.35 : 1,
              transition: 'opacity 0.2s',
              pointerEvents: loading ? 'none' : 'auto',
              minHeight: '400px'
            }}
          >
            {blogs.length === 0 && !loading ? (
              <div className="no-blogs-msg global-text">{t("blog.no_blog_data")}</div>
            ) : (
              blogs.map((b) => {
                const coverUrl = getCoverUrl(b);
                const excerpt = extractExcerpt(b.content);
                return (
                  <Link
                    key={b.id}
                    to={`/blog/${b.slug}`}
                    className="blog-card"
                  >
                    <div className="blog-card-image">
                      {coverUrl ? (
                        <img src={coverUrl} alt={b.title} loading="lazy" />
                      ) : (
                        <div className="blog-card-image-placeholder">&diams;</div>
                      )}
                    </div>
                    <div className="blog-card-body">
                      <div className="blog-card-date global-text">
                        {formatDate(b.updatedAt)}
                      </div>
                      <div className="blog-card-title global-subtitle">{b.title}</div>
                      {excerpt && (
                        <div className="blog-card-excerpt global-text">{excerpt}</div>
                      )}
                      <div className="blog-card-author">
                        <img src={authorAvatar} alt={DEFAULT_AUTHOR_NAME} />
                        <span className="global-text">{DEFAULT_AUTHOR_NAME}</span>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        )}

        {/* Pagination Component */}
        {pageCount > 1 && (
          <div className="blog-pagination-container d-flex justify-content-center mt-5 mb-4">
            <Pagination>
              <Pagination.First disabled={page === 1} onClick={() => setPage(1)} />
              <Pagination.Prev disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />

              {[...Array(pageCount)].map((_, index) => {
                const pageNumber = index + 1;

                if (
                  pageNumber === 1 ||
                  pageNumber === pageCount ||
                  (pageNumber >= page - 2 && pageNumber <= page + 2)
                ) {
                  return (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === page}
                      onClick={() => setPage(pageNumber)}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  );
                }

                if (pageNumber === page - 3 || pageNumber === page + 3) {
                  return <Pagination.Ellipsis key={pageNumber} disabled />;
                }

                return null;
              })}

              <Pagination.Next disabled={page === pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} />
              <Pagination.Last disabled={page === pageCount} onClick={() => setPage(pageCount)} />
            </Pagination>
          </div>
        )}
      </div>
    </Container>
  );
}
